/**
 * Rule Testability Mock
 *
 * Side-by-side comparison of vague vs precise trading rules,
 * showing what makes a rule testable by a machine.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

interface RuleRow {
  vague: string;
  precise: string;
}

const rules: RuleRow[] = [
  {
    vague: 'Buy when the trend looks strong',
    precise: 'Enter long when close > EMA(200) and ADX(14) > 25',
  },
  {
    vague: 'Take profit when it feels right',
    precise: 'Exit when price reaches 2x ATR(14) above entry',
  },
  {
    vague: 'Cut losses if it goes too far',
    precise: 'Stop loss at 1.5x ATR(14) below entry price',
  },
  {
    vague: 'Only trade when momentum is good',
    precise: 'Filter: only take signals when RSI(14) is between 40 and 70',
  },
];

function RuleCell({ text, pass }: { text: string; pass: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.4rem',
      padding: '0.4rem 0.5rem', background: '#181825',
      borderRadius: 4, border: `1px solid ${pass ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'}`,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
        background: pass ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginTop: '0.1rem',
      }}>
        <FontAwesomeIcon
          icon={pass ? faCheck : faXmark}
          style={{ fontSize: '0.45rem', color: pass ? '#22c55e' : '#ef4444' }}
        />
      </div>
      <div style={{
        fontSize: '0.6875rem', color: pass ? '#d1d5db' : '#9ca3af',
        fontStyle: pass ? 'normal' : 'italic',
      }}>
        {text}
      </div>
    </div>
  );
}

export default function RuleTestabilityMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Vague vs Precise Rules
      </div>
      <div className="platform-mock-body" style={{ padding: '1rem' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '0.5rem', marginBottom: '0.5rem',
        }}>
          <div style={{
            fontSize: '0.625rem', fontWeight: 700, color: '#ef4444',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            paddingLeft: '0.5rem',
          }}>Not Testable</div>
          <div style={{
            fontSize: '0.625rem', fontWeight: 700, color: '#22c55e',
            textTransform: 'uppercase', letterSpacing: '0.05em',
            paddingLeft: '0.5rem',
          }}>Testable</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {rules.map((r, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.35rem',
            }}>
              <RuleCell text={r.vague} pass={false} />
              <RuleCell text={r.precise} pass />
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)',
          borderRadius: 6, fontSize: '0.6875rem', color: '#9ca3af',
        }}>
          <span style={{ color: '#22c55e', fontWeight: 600 }}>The test: </span>
          Can a machine execute this rule without asking you a question?
          If not, it needs to be rewritten.
        </div>
      </div>
      <div className="platform-mock-caption">
        Precise rules produce identical results every time. Vague rules produce different results depending on who reads them.
      </div>
    </div>
  );
}
